import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

import itcounsulting from "../img/itcounsulting.jpg";
import cloud from "../img/cloud.jpg";
import bps from "../img/bps.jpg";
import ai from "../img/ai.jpg";
import dm from "../img/dm.jpg";
import cyber from "../img/cyber.jpg";
import csd from "../img/csd.jpg";
import ui from "../img/ui.jpg";

const cardVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const Services = () => {
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const isUserInteracting = useRef(false);

  const services = [
    {
      title: "Custom Software Development",
      description:
        "We build secure, scalable, and innovative software solutions that drive real business growth and digital transformation.",
      mainImage: csd,
      link: "/custom-software-development",
    },
    {
      title: "IT Support & Managed Services",
      description:
        "We help businesses in Hyderabad run smoothly and grow efficiently with reliable, proactive IT support and managed services.",
      mainImage: itcounsulting,
      link: "/it-support-services",
    },
    {
      title: "Cloud & Infrastructure Services",
      description:
        "Trusted cloud service providers delivering secure, scalable, and cost-effective cloud solutions for modern business success.",
      mainImage: cloud,
      link: "/cloud-service-providers",
    },
    {
      title: "Business Process Outsourcing (BPO)",
      description:
        "Boost efficiency, reduce operational costs, and enhance customer satisfaction with our reliable and intelligent BPO solutions.",
      mainImage: bps,
      link: "/bpo-services",
    },
    {
      title: "AI & Data Analytics Solutions",
      description:
        "Unlock real-time insights, predictive intelligence, and smarter workflows with advanced AI-powered data analytics.",
      mainImage: ai,
      link: "/data-analytics-services",
    },
    {
      title: "Digital Marketing Services",
      description:
        "Transform your brand’s digital presence with AI-driven, data-backed strategies that deliver measurable growth and engagement.",
      mainImage: dm,
      link: "/digital-marketing-services",
    },
    {
      title: "UI/UX Design Services",
      description:
        "Get modern, intuitive, and conversion-focused websites & mobile app designs that delight users and elevate your brand.",
      mainImage: ui,
      link: "/ui-ux-design-services",
    },
    {
      title: "Cybersecurity Services",
      description:
        "Protect your business with advanced cybersecurity solutions, proactive threat detection, and complete data assurance.",
      mainImage: cyber,
      link: "/cybersecurity-services",
    },
  ];

  /** Duplicate for infinite loop */
  const loopServices = [...services, ...services];

  /** Auto-scroll effect */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const speed = 0.6;

    const autoScroll = () => {
      if (!isUserInteracting.current) {
        el.scrollLeft += speed;

        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      animationRef.current = requestAnimationFrame(autoScroll);
    };

    animationRef.current = requestAnimationFrame(autoScroll);

    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Leading Software Development Company in Hyderabad | SecurXperts</title>
        <meta
          name="description"
          content="SecurXperts offers custom software development, IT support, cloud solutions, cybersecurity, UI/UX design, digital marketing, and training in Hyderabad."
        />
      </Helmet>

      <section className="py-16 md:py-20 bg-white text-gray-900 -mt-5" id="services">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 md:mb-16 gap-8">
            <div className="w-full lg:w-3/4">
              <h2 className="text-3xl font-semibold uppercase text-gray-500 mb-1">
                OUR SERVICES
              </h2>
                        

              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 pb-2">
                Innovation That Drives Real Results
              </h2>
              <p>Our comprehensive range of digital services is designed to help businesses innovate, optimize operations and stay competitive in a rapidly evolving digital landscape.</p>
            </div>

            <Link
              to="/our-services"
              className="inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 shadow-md transition"
            >
              VIEW ALL SERVICES →
            </Link>
          </div>

          {/* Horizontal Auto Scroll (No Buttons, No Scrollbar) */}
          <motion.div
            ref={scrollRef}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            transition={{ staggerChildren: 0.1 }}
            className="flex flex-nowrap gap-8 pb-4 overflow-x-auto
              [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {loopServices.map((service, index) => (
<motion.div
  key={index}
  variants={cardVariants}
  className="flex-shrink-0 w-72 md:w-80 lg:w-72"
  onMouseEnter={() => {
    isUserInteracting.current = true;
  }}
  onMouseLeave={() => {
    isUserInteracting.current = false;
  }}
>

                <div className="bg-white rounded-xl shadow-xl border border-gray-100 pb-6 flex flex-col h-full
                  transition-all duration-500 group hover:-translate-y-1 hover:shadow-blue-500/50">

                  <div className="relative w-full h-40 rounded-t-xl overflow-hidden">
                    <img
                      src={service.mainImage}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>

                  <div className="p-4 pt-16 flex flex-col items-center text-center flex-grow">
                    <h4 className="text-xl font-bold mb-3">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 mb-4 px-2 flex-grow">
                      {service.description}
                    </p>
                    <Link
                      to={service.link}
                      className="text-blue-600 font-semibold text-sm hover:text-blue-800"
                    >
                      READ MORE →
                    </Link>
                  </div>

                </div>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default Services;
